import type React from "react";
import {
  Image,
  type ImageSourcePropType,
  Pressable,
  Text,
  View,
} from "react-native";
import { ChevronDown, ChevronUp } from "lucide-react-native";
import { Collapsible, Row } from "@louise/moon-ui";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { useTaskSectionState } from "../hooks/useTaskSectionState";

type TimelineSubtaskCardProps = {
  id: string;
  title: string;
  subtitle?: string;
  titleIcon?: React.ReactNode;
  titleImage?: ImageSourcePropType;
  defaultExpanded?: boolean;
  collapsedPreviewHeight?: number;
  children: React.ReactNode;
};

export function TimelineSubtaskCard({
  id,
  title,
  subtitle,
  titleIcon,
  titleImage,
  defaultExpanded = true,
  children,
}: TimelineSubtaskCardProps) {
  const t = useTranslations();
  const capturePress = usePressCapture("TimelineSubtaskCard");
  const { expanded, toggleExpanded } = useTaskSectionState(id, {
    defaultExpanded,
  });

  return (
    <View
      style={{
        ...(GLASS_CARD_STYLE as Record<string, unknown>),
        borderRadius: 18,
        padding: 16,
        overflow: "hidden",
      }}
    >
      <Pressable
        onPress={() => {
          capturePress(pressLabel(title), id);
          toggleExpanded();
        }}
        accessibilityRole="button"
        accessibilityLabel={
          expanded
            ? t("homepage.task_section_card.collapse_title", {
                params: { title },
              })
            : t("homepage.task_section_card.expand_title", {
                params: { title },
              })
        }
        accessibilityState={{ expanded }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <View
            style={{
              flex: 1,
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
            }}
          >
            {titleIcon ? (
              <View
                style={{
                  width: 36,
                  height: 42,
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {titleIcon}
              </View>
            ) : titleImage ? (
              <Image
                source={titleImage}
                resizeMode="contain"
                style={{ width: 24, height: 42, flexShrink: 0 }}
              />
            ) : null}
            <View style={{ flex: 1 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 17,
                  lineHeight: 22,
                  color: EMBER.colors.text,
                  fontWeight: "700",
                }}
              >
                {title}
              </Text>
              {subtitle ? (
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    lineHeight: 18,
                    color: EMBER.colors.textLight,
                    fontWeight: "400",
                    marginTop: 3,
                  }}
                >
                  {subtitle}
                </Text>
              ) : null}
            </View>
          </View>

          <View
            style={{
              width: 22,
              height: 22,
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {expanded ? (
              <ChevronUp
                size={18}
                color={EMBER.colors.textLight}
                strokeWidth={1.6}
              />
            ) : (
              <ChevronDown
                size={18}
                color={EMBER.colors.textLight}
                strokeWidth={1.6}
              />
            )}
          </View>
        </View>
      </Pressable>

      <Collapsible expanded={expanded}>
        <View style={{ paddingTop: 14 }}>{children}</View>
      </Collapsible>
    </View>
  );
}

export function TimelineActionRow({
  title,
  subtitle,
  icon,
  onPress,
}: {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  onPress: () => void;
}) {
  return (
    <Row
      title={title}
      subtitle={subtitle}
      leading={icon}
      leadingSize={icon ? 36 : 0}
      gap={10}
      paddingHorizontal={16}
      paddingVertical={12}
      borderRadius={18}
      onPress={onPress}
      accessibilityLabel={title}
    />
  );
}
